/**
 * DateRangeFilter Component
 *
 * Provides flexible date range filtering options for the dashboard:
 * - Today
 * - This Week
 * - This Month
 * - Last 3 Months
 * - Custom Date Range
 *
 * Emits selected date range via callback function
 */

import React, { useState, useRef, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Calendar, X, ChevronDown } from 'lucide-react';

export interface DateRange {
  startDate: string; // YYYY-MM-DD format
  endDate: string;   // YYYY-MM-DD format
  label: string;
}

interface DateRangeFilterProps {
  onDateRangeChange: (dateRange: DateRange) => void;
  selectedLabel?: string;
}

/**
 * Get today's date in YYYY-MM-DD format (local timezone)
 */
function getTodayDate(): string {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  const result = `${year}-${month}-${day}`;
  console.log('📅 getTodayDate():', result);
  return result;
}

/**
 * Get the start of current week (Monday) in YYYY-MM-DD format
 */
function getWeekStartDate(): string {
  const now = new Date();
  const dayOfWeek = now.getDay(); // 0 = Sunday, 1 = Monday, ... 6 = Saturday

  // Calculate days to subtract to get to Monday
  // If Sunday (0), go back 6 days to previous Monday
  // Otherwise, go back (dayOfWeek - 1) days
  const daysToSubtract = dayOfWeek === 0 ? 6 : dayOfWeek - 1;

  const weekStart = new Date(now);
  weekStart.setDate(now.getDate() - daysToSubtract);

  const year = weekStart.getFullYear();
  const month = String(weekStart.getMonth() + 1).padStart(2, '0');
  const day = String(weekStart.getDate()).padStart(2, '0');
  const result = `${year}-${month}-${day}`;

  console.log('📅 getWeekStartDate(): today is', now.toDateString(),
    '(day', dayOfWeek, '), week starts on', result);
  return result;
}

/**
 * Get the start of current month in YYYY-MM-DD format
 */
function getMonthStartDate(): string {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const result = `${year}-${month}-01`;
  console.log('📅 getMonthStartDate():', result);
  return result;
}

/**
 * Get date from 3 months ago in YYYY-MM-DD format
 */
function getThreeMonthsAgoDate(): string {
  const now = new Date();
  const threeMonthsAgo = new Date(now.getFullYear(), now.getMonth() - 3, now.getDate());

  const year = threeMonthsAgo.getFullYear();
  const month = String(threeMonthsAgo.getMonth() + 1).padStart(2, '0');
  const day = String(threeMonthsAgo.getDate()).padStart(2, '0');
  const result = `${year}-${month}-${day}`;

  console.log('📅 getThreeMonthsAgoDate():', result);
  return result;
}

/**
 * Get date from 1 year ago in YYYY-MM-DD format (for "All Time" option)
 */
function getOneYearAgoDate(): string {
  const now = new Date();
  const oneYearAgo = new Date(now.getFullYear() - 1, now.getMonth(), now.getDate());

  const year = oneYearAgo.getFullYear();
  const month = String(oneYearAgo.getMonth() + 1).padStart(2, '0');
  const day = String(oneYearAgo.getDate()).padStart(2, '0');
  const result = `${year}-${month}-${day}`;

  console.log('📅 getOneYearAgoDate():', result);
  return result;
}

/**
 * DateRangeFilter Component
 */
export const DateRangeFilter: React.FC<DateRangeFilterProps> = ({
  onDateRangeChange,
  selectedLabel = 'This Month',
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [currentLabel, setCurrentLabel] = useState<string>(selectedLabel);
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [showCustomInput, setShowCustomInput] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Predefined range labels (dates are calculated fresh when clicked)
  const predefinedLabels = ['Today', 'This Week', 'This Month', 'Last 3 Months', 'All Time'];

  /**
   * Calculate date range for a given label - FRESH calculation on each call
   */
  const getDateRangeForLabel = (label: string): DateRange => {
    const today = getTodayDate();

    switch (label) {
      case 'Today':
        return { label, startDate: today, endDate: today };
      case 'This Week':
        return { label, startDate: getWeekStartDate(), endDate: today };
      case 'This Month':
        return { label, startDate: getMonthStartDate(), endDate: today };
      case 'Last 3 Months':
        return { label, startDate: getThreeMonthsAgoDate(), endDate: today };
      case 'All Time':
        return { label, startDate: getOneYearAgoDate(), endDate: today };
      default:
        return { label, startDate: today, endDate: today };
    }
  };

  /**
   * Close dropdown when clicking outside
   */
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [isOpen]);

  /**
   * Handle predefined range selection - calculates dates FRESH on click
   */
  const handleSelectRange = (label: string) => {
    // Calculate the date range FRESH at click time
    const range = getDateRangeForLabel(label);

    console.log('🎯 Selected range:', label);
    console.log('   startDate:', range.startDate);
    console.log('   endDate:', range.endDate);

    setCurrentLabel(range.label);
    setShowCustomInput(false);
    setCustomStartDate('');
    setCustomEndDate('');
    onDateRangeChange(range);
    setIsOpen(false);
  };

  /**
   * Handle custom date range
   */
  const handleApplyCustomRange = () => {
    if (customStartDate && customEndDate) {
      // Validate date range
      if (new Date(customStartDate) > new Date(customEndDate)) {
        alert('Start date must be before end date');
        return;
      }

      const customRange: DateRange = {
        label: `${formatDisplayDate(customStartDate)} - ${formatDisplayDate(customEndDate)}`,
        startDate: customStartDate,
        endDate: customEndDate,
      };

      setCurrentLabel(customRange.label);
      onDateRangeChange(customRange);
      setIsOpen(false);
    }
  };

  /**
   * Clear custom date inputs
   */
  const handleClearCustom = () => {
    setCustomStartDate('');
    setCustomEndDate('');
    setShowCustomInput(false);
  };

  /**
   * Format date for display (e.g., "Jan 15, 2025")
   */
  function formatDisplayDate(dateString: string): string {
    if (!dateString) return '';
    const date = new Date(dateString + 'T00:00:00');
    return date.toLocaleDateString('en-IN', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  }

  /**
   * Toggle dropdown
   */
  const toggleDropdown = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsOpen(!isOpen);
  };

  /**
   * Close dropdown
   */
  const closeDropdown = () => {
    setIsOpen(false);
  };

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Filter Button */}
      <Button
        variant="outline"
        onClick={toggleDropdown}
        className="flex items-center gap-2 bg-white border-gray-200 hover:bg-gray-50"
      >
        <Calendar className="w-4 h-4 text-gray-600" />
        <span className="text-sm font-medium text-gray-700">{currentLabel}</span>
        <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </Button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div className="absolute top-full left-0 mt-2 w-56 bg-white border border-gray-200 rounded-md shadow-lg z-50">
          {/* Quick Select Section */}
          <div className="px-2 py-2 border-b border-gray-200">
            <div className="px-2 py-1.5 text-xs font-semibold text-gray-700">Quick Select</div>
            <div className="space-y-1 mt-1">
              {predefinedLabels.map((label) => (
                <button
                  key={label}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleSelectRange(label);
                  }}
                  className={`w-full text-left px-3 py-2 rounded-md text-sm transition-colors ${
                    currentLabel === label
                      ? 'bg-blue-100 text-blue-900 font-medium'
                      : 'text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>

          {/* Custom Range Section */}
          <div className="px-2 py-2">
            <div className="px-2 py-1.5 text-xs font-semibold text-gray-700">Custom Range</div>

            {!showCustomInput ? (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setShowCustomInput(true);
                }}
                className="w-full text-left px-3 py-2 rounded-md text-sm text-gray-700 hover:bg-gray-100 transition-colors"
              >
                Select Custom Date Range
              </button>
            ) : (
              <div className="space-y-3 mt-3 p-3 bg-gray-50 rounded-md">
                {/* Start Date Input */}
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1.5">
                    Start Date
                  </label>
                  <input
                    type="date"
                    value={customStartDate}
                    onChange={(e) => setCustomStartDate(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                {/* End Date Input */}
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1.5">
                    End Date
                  </label>
                  <input
                    type="date"
                    value={customEndDate}
                    onChange={(e) => setCustomEndDate(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                {/* Action Buttons */}
                <div className="flex gap-2 pt-2">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleApplyCustomRange();
                    }}
                    className="flex-1 px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white text-xs font-medium rounded-md transition-colors"
                  >
                    Apply
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleClearCustom();
                    }}
                    className="flex-1 px-3 py-2 border border-gray-300 text-gray-700 text-xs font-medium rounded-md hover:bg-gray-100 transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default DateRangeFilter;
